// Phase 8 R4 — small star button for favoriting/unfavoriting a canned job.
// Stops propagation so it can sit inside a clickable Quick Apply row.

import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToggleCannedJobFavorite } from '@/hooks/cannedJobs/useToggleCannedJobFavorite';

interface Props {
  shopId: string;
  cannedJobId: string;
  isFavorite: boolean;
  size?: number;
  className?: string;
  /** Optional accessible label override. */
  ariaLabel?: string;
}

export function FavoriteToggle({
  shopId,
  cannedJobId,
  isFavorite,
  size = 16,
  className,
  ariaLabel,
}: Props) {
  const mut = useToggleCannedJobFavorite();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (mut.isPending) return;
    mut.mutate({ shopId, cannedJobId, isFavorite: !isFavorite });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel ?? (isFavorite ? 'Unfavorite canned job' : 'Favorite canned job')}
      aria-pressed={isFavorite}
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1 transition-colors',
        'hover:bg-accent text-muted-foreground',
        isFavorite && 'text-primary',
        className,
      )}
    >
      <Star
        size={size}
        className={cn('transition-transform', isFavorite && 'fill-current')}
      />
    </button>
  );
}
